import { useCallback, useEffect, useRef, useState } from 'react';

import { EMAIL_PLATFORMS } from '@this+that/constants';
import { MessagingPlatform, UserCommunication } from '@this+that/models';

//import { APP_PAGES } from '../../../routes/constants';
import { useGetUserCommunicationById as useGetUserCommunicationByIdService } from '../../../services/userCommunications.service';
import { useUserCommunicationsStore } from '../../../store';
import { useCommunicationIntentsStore } from '../../../store/Inbox/CommunicationIntents';
import { indexedDBAdapter } from '../../../store/utils/indexedDBAdapter';

enum RefetchOutcomeStatus {
  Found = 'found',
  NotFound = 'not-found',
  Inconclusive = 'inconclusive',
}

interface RefetchOutcome {
  status: RefetchOutcomeStatus;
  userCommunication: UserCommunication | null;
}

interface UseGetUserCommunicationByIdResult {
  userCommunication: UserCommunication | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<RefetchOutcome>;
  isReady: boolean;
  isNotFound: boolean;
}

export const useGetUserCommunicationById = (
  id: string,
): UseGetUserCommunicationByIdResult => {
  const { removeAndInsertUserCommunication, getUserCommunicationByMessageId } =
    useUserCommunicationsStore();

  const { getUserCommunicationById: apiGetUserCommunication } =
    useGetUserCommunicationByIdService();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);
  // calculateViews can silently drop an archived/DoBox-resolved message from every store bucket; keep it here so a fetched message still renders.
  const [fetchedUserCommunication, setFetchedUserCommunication] =
    useState<UserCommunication | null>(null);
  const [isNotFound, setIsNotFound] = useState(false);

  // Last communication resolved for the current id, so a transient store
  // disappearance (WS REMOVE, wholesale slice replace) keeps rendering it
  // instead of momentarily falling through to `null`.
  const lastKnownUserCommunicationRef = useRef<UserCommunication | null>(null);
  // True once the store has genuinely held this id, as opposed to a message
  // that only ever exists via `fetchedUserCommunication` (e.g. an archived
  // message calculateViews drops from every bucket) — only the former is a
  // "disappearance" worth recovering from.
  const wasInStoreRef = useRef(false);
  const recoveryAttemptedRef = useRef(false);

  const userCommunicationFromStore = getUserCommunicationByMessageId(id);

  const fetchUserCommunication = useCallback(async () => {
    if (!id?.trim()) {
      setIsReady(true);
      return;
    }

    if (userCommunicationFromStore) {
      const isEmailPlatform = EMAIL_PLATFORMS.includes(
        userCommunicationFromStore.platform as MessagingPlatform,
      );
      const hasRequiredData =
        !isEmailPlatform || userCommunicationFromStore.s3Key;

      if (hasRequiredData) {
        setIsReady(true);
        return;
      }
    }

    setIsLoading(true);
    setError(null);

    try {
      const fetched = await apiGetUserCommunication(id);

      if (fetched) {
        //if (window.location.pathname.includes(APP_PAGES.DOBOX)) {
        //  return;
        //}
        const { communication: reconciled } = useCommunicationIntentsStore
          .getState()
          .reconcileCommunication(fetched);
        removeAndInsertUserCommunication(reconciled);
        setFetchedUserCommunication(reconciled);
      } else if (!userCommunicationFromStore) {
        setIsNotFound(true);
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to fetch user communication',
      );
      if (!userCommunicationFromStore) {
        setIsNotFound(true);
      }
    } finally {
      setIsLoading(false);
      setIsReady(true);
    }
  }, [
    id,
    userCommunicationFromStore,
    apiGetUserCommunication,
    removeAndInsertUserCommunication,
  ]);

  const refetch = useCallback(async (): Promise<RefetchOutcome> => {
    if (!id?.trim()) {
      return {
        status: RefetchOutcomeStatus.Inconclusive,
        userCommunication: null,
      };
    }

    setIsLoading(true);
    setError(null);

    try {
      const fetched = await apiGetUserCommunication(id);

      if (!fetched) {
        return {
          status: RefetchOutcomeStatus.NotFound,
          userCommunication: null,
        };
      }

      const { communication: reconciled } = useCommunicationIntentsStore
        .getState()
        .reconcileCommunication(fetched);
      removeAndInsertUserCommunication(reconciled);
      setFetchedUserCommunication(reconciled);
      return {
        status: RefetchOutcomeStatus.Found,
        userCommunication: reconciled,
      };
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to fetch user communication',
      );
      // A thrown request (network blip, timeout) is inconclusive, not a
      // confirmed absence — never treat it the same as an API-confirmed null.
      return {
        status: RefetchOutcomeStatus.Inconclusive,
        userCommunication: null,
      };
    } finally {
      setIsLoading(false);
    }
  }, [id, apiGetUserCommunication, removeAndInsertUserCommunication]);

  // Ref-latest pattern: the recovery effect below calls this without listing
  // it in a dependency array, since apiGetUserCommunication (Apollo's
  // refetch under the hood) is not guaranteed to be a stable identity.
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  useEffect(() => {
    setIsReady(false);
    setFetchedUserCommunication(null);
    setIsNotFound(false);
    lastKnownUserCommunicationRef.current = null;
    wasInStoreRef.current = false;
    recoveryAttemptedRef.current = false;
  }, [id]);

  useEffect(() => {
    if (!isReady && !isLoading) {
      fetchUserCommunication();
    }
  }, [fetchUserCommunication, isReady, isLoading]);

  const resolvedUserCommunication =
    userCommunicationFromStore ?? fetchedUserCommunication ?? null;

  useEffect(() => {
    if (resolvedUserCommunication) {
      lastKnownUserCommunicationRef.current = resolvedUserCommunication;
    }
  }, [resolvedUserCommunication]);

  useEffect(() => {
    if (userCommunicationFromStore) {
      wasInStoreRef.current = true;
      // Reset per-disappearance, not per-id: the row being present again
      // means a *later* disappearance (another WS event, or a legitimate
      // delete) must get its own recovery attempt.
      recoveryAttemptedRef.current = false;
      setIsNotFound(false);
    }
  }, [userCommunicationFromStore]);

  useEffect(() => {
    if (!isReady || !id?.trim() || userCommunicationFromStore) {
      return;
    }
    if (!wasInStoreRef.current || recoveryAttemptedRef.current) {
      return;
    }

    recoveryAttemptedRef.current = true;

    const recoverUserCommunication = async () => {
      const deletedLocally = await indexedDBAdapter.isDeletedLocally(id);
      if (deletedLocally) {
        setIsNotFound(true);
        return;
      }

      const outcome = await refetchRef.current();

      if (outcome.status === RefetchOutcomeStatus.NotFound) {
        setIsNotFound(true);
        return;
      }

      if (outcome.status === RefetchOutcomeStatus.Inconclusive) {
        // Not a confirmed absence — release the guard so a later store
        // change (reconnect, retry) can attempt recovery again, without
        // retrying immediately in this same cycle.
        recoveryAttemptedRef.current = false;
      }
    };

    recoverUserCommunication();
  }, [isReady, id, userCommunicationFromStore]);

  return {
    userCommunication:
      resolvedUserCommunication ?? lastKnownUserCommunicationRef.current,
    isLoading,
    error,
    refetch,
    isReady,
    isNotFound,
  };
};
